import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import Login from '../pages/Login';
import Dashboard from '../pages/Dashboard';
import MainLayout from '../components/layout/MainLayout';
import IsLoggedIn from '../components/auth/IsLoggedIn';
import { Helmet } from 'react-helmet';
import TestingPage from '../pages/TestingPage';
import CreateCrewDelivery from '../pages/CrewDelivery/create/CreateCrewDelivery';
import CreateCrewReturn from '../pages/CrewReturn/create/CreateCrewReturn';
import BookGoodsReceipt from '../pages/Stock/book-goods-receipt/BookGoodsReceipt';
import StockCorrection from '../pages/Stock/correction/StockCorrection';
import StockTransfer from '../pages/Stock/transfer/StockTransfer';
import { useSelector } from 'react-redux';
import CrewDeliveryDetails from '../pages/CrewDelivery/details/CrewDeliveryDetails';
import CrewDeliveryPdf from '../pages/CrewDelivery/details/CrewDeliveryPdf';
import ListCrewDeliveries from '../pages/CrewDelivery/list/ListCrewDeliveries';
import ListStocks from '../pages/Stock/list/ListStocks';
import ListStockMovements from '../pages/Stock/movements/ListStockMovements';
import InventoryCountSheet from '../pages/Inventory/InventoryCountSheet';
import InventoryCountList from '../pages/Inventory/InventoryCountList';
import InventoryCountDetail from '../pages/Inventory/InventoryCountDetail';
import MarktLayout from '../components/layout/MarktLayout';
import MarktDashboard from '../pages/Markt/MarktDashboard';
import MarktGoodsReceipt from '../pages/Markt/goods-receipt/MarktGoodsReceipt';
import MarktStockOverview from '../pages/Markt/stock-overview';
import MarktPOSInterface from '../pages/Markt/sell';
import CashRegisterOverview from '../pages/Markt/cash-register';
import CashRegisterSessionDetail from '../pages/Markt/cash-register/SessionDetail';
import ReservationManagement from '../pages/Markt/reservations';
import PublicReservation from '../pages/PublicReservation';
import RequireGroup from '../components/auth/RequireGroup';
import ListCrewLocations from '../pages/Locations';
import CrewLocationDetails from '../pages/Locations/LocationDetails';
import BackstageLocationDetails from '../pages/Locations/BackstageLocationDetails';
import LocationReport from '../pages/Locations/LocationReport';
import ContingentManagement from '../pages/Contingent/ContingentManagement';
import TodoBoard from '../pages/Todo/TodoBoard';
import CreateTodo from '../pages/Todo/CreateTodo';
import TodoDetail from '../pages/Todo/TodoDetail';
import MobileTodoList from '../pages/Todo/MobileTodoList';
import Logbook from '../pages/Logbook/Logbook';
import BackstageDeliveryPlanner from '../pages/BackstageDelivery/BackstageDeliveryPlanner';
import BarDeliveryForm from '../pages/BarDelivery/create/BarDeliveryForm';
import ListBarDeliveries from '../pages/BarDelivery/list/ListBarDeliveries';
import BarDeliveryDetails from '../pages/BarDelivery/details/BarDeliveryDetails';
import BarLocationDetails from '../pages/Locations/BarLocationDetails';
import BarBillingReport from '../pages/BarBilling/BarBillingReport';
import BarDepositOverview from '../pages/BarBilling/BarDepositOverview';
import LocationImport from '../pages/Admin/LocationImport';
import NotFound from '../pages/NotFound';

const routes = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    errorElement: <NotFound />,
    children: [
      {
        path: '/login',
        element: <Login />,
      },
      {
        path: '/',
        element: (
          <IsLoggedIn withNavigate>
            <Dashboard />
          </IsLoggedIn>
        ),
      },
      {
        path: '/testing',
        element: (
          <IsLoggedIn withNavigate>
            <TestingPage />
          </IsLoggedIn>
        ),
      },
      {
        path: '/crew-delivery/create',
        element: (
          <IsLoggedIn withNavigate>
            <CreateCrewDelivery />
          </IsLoggedIn>
        ),
      },
      {
        path: '/crew-delivery/return/create',
        element: (
          <IsLoggedIn withNavigate>
            <CreateCrewReturn />
          </IsLoggedIn>
        ),
      },
      {
        path: '/crew-delivery/:deliveryId',
        element: (
          <IsLoggedIn withNavigate>
            <CrewDeliveryDetails />
          </IsLoggedIn>
        ),
      },
      {
        path: '/crew-delivery/last/:lastN',
        element: (
          <IsLoggedIn withNavigate>
            <ListCrewDeliveries />
          </IsLoggedIn>
        ),
      },
      {
        path: '/crew-delivery/open',
        element: (
          <IsLoggedIn withNavigate>
            <ListCrewDeliveries onlyOpen={true} />
          </IsLoggedIn>
        ),
      },
      {
        path: '/crew-delivery/:deliveryId/confirm',
        element: (
          <IsLoggedIn withNavigate>
            <CrewDeliveryDetails confirm={true} />
          </IsLoggedIn>
        ),
      },
      {
        path: '/goods-receipt/book',
        element: (
          <IsLoggedIn withNavigate>
            <RequireGroup group='inventory'>
              <BookGoodsReceipt />
            </RequireGroup>
          </IsLoggedIn>
        ),
      },
      {
        path: '/stock/correction',
        element: (
          <IsLoggedIn withNavigate>
            <RequireGroup group='inventory'>
              <StockCorrection />
            </RequireGroup>
          </IsLoggedIn>
        ),
      },
      {
        path: '/stock/transfer',
        element: (
          <IsLoggedIn withNavigate>
            <RequireGroup group='inventory'>
              <StockTransfer />
            </RequireGroup>
          </IsLoggedIn>
        ),
      },
      {
        path: '/stock',
        element: (
          <IsLoggedIn withNavigate>
            <ListStocks />
          </IsLoggedIn>
        ),
      },
      {
        path: '/stock/movements',
        element: (
          <IsLoggedIn withNavigate>
            <RequireGroup group='inventory'>
              <ListStockMovements />
            </RequireGroup>
          </IsLoggedIn>
        ),
      },
      {
        path: '/inventory-count',
        element: (
          <IsLoggedIn withNavigate>
            <RequireGroup group='inventory'>
              <InventoryCountSheet />
            </RequireGroup>
          </IsLoggedIn>
        ),
      },
      {
        path: '/inventory-counts',
        element: (
          <IsLoggedIn withNavigate>
            <RequireGroup group='inventory'>
              <InventoryCountList />
            </RequireGroup>
          </IsLoggedIn>
        ),
      },
      {
        path: '/inventory-counts/:id',
        element: (
          <IsLoggedIn withNavigate>
            <RequireGroup group='inventory'>
              <InventoryCountDetail />
            </RequireGroup>
          </IsLoggedIn>
        ),
      },
      {
        path: '/crew-locations',
        element: (
          <IsLoggedIn withNavigate>
            <ListCrewLocations />
          </IsLoggedIn>
        ),
      },
      {
        path: '/crew-location/:locationId',
        element: (
          <IsLoggedIn withNavigate>
            <CrewLocationDetails />
          </IsLoggedIn>
        ),
      },
      {
        path: '/backstage-location/:locationId',
        element: (
          <IsLoggedIn withNavigate>
            <BackstageLocationDetails />
          </IsLoggedIn>
        ),
      },
      {
        path: '/bar-location/:locationId',
        element: (
          <IsLoggedIn withNavigate>
            <BarLocationDetails />
          </IsLoggedIn>
        ),
      },
      // Bar deliveries. Static segments outrank the :deliveryId param in React
      // Router's ranking, so /bar-delivery/list never hits the detail route.
      {
        path: '/bar-delivery/create',
        element: (
          <IsLoggedIn withNavigate>
            <BarDeliveryForm deliveryType='OUT' />
          </IsLoggedIn>
        ),
      },
      {
        path: '/bar-delivery/return/create',
        element: (
          <IsLoggedIn withNavigate>
            <BarDeliveryForm deliveryType='RET' />
          </IsLoggedIn>
        ),
      },
      {
        path: '/bar-delivery/empties/create',
        element: (
          <IsLoggedIn withNavigate>
            <BarDeliveryForm deliveryType='EMP' />
          </IsLoggedIn>
        ),
      },
      {
        path: '/bar-delivery/list',
        element: (
          <IsLoggedIn withNavigate>
            <ListBarDeliveries />
          </IsLoggedIn>
        ),
      },
      {
        path: '/bar-delivery/open',
        element: (
          <IsLoggedIn withNavigate>
            <ListBarDeliveries onlyOpen />
          </IsLoggedIn>
        ),
      },
      {
        path: '/bar-delivery/:deliveryId',
        element: (
          <IsLoggedIn withNavigate>
            <BarDeliveryDetails />
          </IsLoggedIn>
        ),
      },
      {
        path: '/bar-delivery/:deliveryId/confirm',
        element: (
          <IsLoggedIn withNavigate>
            <BarDeliveryDetails confirm />
          </IsLoggedIn>
        ),
      },
      {
        path: '/report/bar-billing',
        element: (
          <IsLoggedIn withNavigate>
            <BarBillingReport />
          </IsLoggedIn>
        ),
      },
      {
        path: '/report/bar-deposit',
        element: (
          <IsLoggedIn withNavigate>
            <BarDepositOverview />
          </IsLoggedIn>
        ),
      },
      {
        // NOT under /admin: the production ingress routes that prefix to the
        // API service, so an /admin/* SPA path 404s in Django instead of
        // falling back to index.html.
        path: '/manage/location-import',
        element: (
          <IsLoggedIn withNavigate>
            <RequireGroup group='admin'>
              <LocationImport />
            </RequireGroup>
          </IsLoggedIn>
        ),
      },
      {
        path: '/contingent',
        element: (
          <IsLoggedIn withNavigate>
            <ContingentManagement />
          </IsLoggedIn>
        ),
      },
      {
        path: '/report/deliveries',
        element: (
          <IsLoggedIn withNavigate>
            <LocationReport />
          </IsLoggedIn>
        ),
      },
      {
        path: '/todo',
        element: (
          <IsLoggedIn withNavigate>
            <TodoBoard />
          </IsLoggedIn>
        ),
      },
      {
        path: '/todo/new',
        element: (
          <IsLoggedIn withNavigate>
            <CreateTodo />
          </IsLoggedIn>
        ),
      },
      {
        path: '/todo/:id',
        element: (
          <IsLoggedIn withNavigate>
            <TodoDetail />
          </IsLoggedIn>
        ),
      },
      {
        path: '/m/todo',
        element: (
          <IsLoggedIn withNavigate>
            <MobileTodoList />
          </IsLoggedIn>
        ),
      },
      {
        path: '/logbook',
        element: (
          <IsLoggedIn withNavigate>
            <Logbook />
          </IsLoggedIn>
        ),
      },
      {
        path: '/backstage-delivery',
        element: (
          <IsLoggedIn withNavigate>
            <RequireGroup group='backstage'>
              <BackstageDeliveryPlanner />
            </RequireGroup>
          </IsLoggedIn>
        ),
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
  {
    path: '/crew-delivery/:deliveryId/pdf',
    element: (
      <IsLoggedIn withNavigate>
        <CrewDeliveryPdf />
      </IsLoggedIn>
    ),
  },
  {
    path: '/markt',
    element: (
      <RequireGroup group='markt'>
        <MarktLayout />
      </RequireGroup>
    ),
    errorElement: <NotFound />,
    children: [
      {
        index: true,
        element: (
          <IsLoggedIn withNavigate>
            <MarktDashboard />
          </IsLoggedIn>
        ),
      },
      {
        path: 'goods-receipt',
        element: (
          <IsLoggedIn withNavigate>
            <MarktGoodsReceipt />
          </IsLoggedIn>
        ),
      },
      {
        path: 'stock',
        element: (
          <IsLoggedIn withNavigate>
            <MarktStockOverview />
          </IsLoggedIn>
        ),
      },
      {
        path: 'sell',
        element: (
          <IsLoggedIn withNavigate>
            <MarktPOSInterface />
          </IsLoggedIn>
        ),
      },
      {
        path: 'cash-register/overview',
        element: (
          <IsLoggedIn withNavigate>
            <CashRegisterOverview />
          </IsLoggedIn>
        ),
      },
      {
        path: 'cash-register/session/:sessionId',
        element: (
          <IsLoggedIn withNavigate>
            <CashRegisterSessionDetail />
          </IsLoggedIn>
        ),
      },
      {
        path: 'reservations',
        element: (
          <IsLoggedIn withNavigate>
            <ReservationManagement />
          </IsLoggedIn>
        ),
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
  {
    // Public, login-free reservation page (no layout / nav, no auth gate).
    path: '/markt/reserve',
    element: <PublicReservation />,
  },
]);

function App() {
  const useDarkMode = useSelector((state) => state.auth.useDarkMode);

  useEffect(() => {
    const rootHtml = document.documentElement;

    rootHtml.setAttribute('data-bs-theme', useDarkMode ? 'dark' : 'light');
  }, [useDarkMode]);

  return (
    <div className='App d-flex flex-column'>
      <Helmet>
        <title>GK Office</title>
      </Helmet>
      <RouterProvider router={routes} />
    </div>
  );
}

export default App;
